import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowRight,
  Check,
  ChevronDown,
  ImageIcon,
  MessageCircle,
  Globe,
  Search,
  Share2,
  UserPlus,
  Building2,
  Upload,
  CheckCircle2,
  Link2,
  Smartphone,
  Gift,
} from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { SiteFooter } from '../components/SiteFooter'
import { CatalogShowcaseGrid } from '../components/CatalogShowcaseGrid'
import { PLANS } from '../lib/plans'

const API_URL = import.meta.env.VITE_API_URL || '/api'
const REGISTER_URL = '/register'

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
}

const painPoints = [
  { icon: ImageIcon, text: 'Harus kirim foto produk satu per satu' },
  { icon: MessageCircle, text: 'Customer bertanya produk yang sama berulang kali' },
  { icon: MessageCircle, text: 'Sulit menjelaskan produk melalui WhatsApp' },
  { icon: Globe, text: 'Belum memiliki website usaha' },
  { icon: Search, text: 'Produk sulit ditemukan calon pelanggan' },
  { icon: Share2, text: 'Informasi usaha tersebar di banyak tempat' },
]

const benefits = [
  'Semua produk dalam satu link',
  'Mudah dibagikan ke WhatsApp',
  'Profil usaha terlihat lebih profesional',
  'Dapat diakses dari HP dan komputer',
  'Menampilkan informasi usaha dan produk dalam satu halaman',
  'Cocok untuk UMKM, reseller, sales motor, sales properti, distributor dan usaha rumahan',
]

const steps = [
  { icon: UserPlus, label: 'Daftar' },
  { icon: Building2, label: 'Isi Profil Usaha' },
  { icon: Upload, label: 'Upload Produk' },
  { icon: CheckCircle2, label: 'Katalog Siap Digunakan' },
  { icon: Share2, label: 'Bagikan Link ke Customer' },
]

const faqs = [
  {
    q: 'Apakah saya harus punya website?',
    a: 'Tidak. Sistem akan membuat katalog online untuk usaha Anda.',
  },
  {
    q: 'Apakah bisa digunakan dari HP?',
    a: 'Ya, seluruh fitur dapat digunakan melalui HP maupun komputer.',
  },
  {
    q: 'Apakah produk saya langsung muncul di Google?',
    a: 'Halaman katalog akan dioptimalkan agar dapat diindeks Google, namun proses indeks mengikuti kebijakan dan waktu Google.',
  },
  { q: 'Apakah bisa untuk reseller?', a: 'Ya.' },
  { q: 'Apakah bisa untuk sales motor?', a: 'Ya.' },
  { q: 'Apakah bisa untuk properti?', a: 'Ya.' },
  { q: 'Apakah ada biaya bulanan?', a: 'Tidak ada biaya bulanan. Paket Gratis Rp0 bisa dipakai selamanya. Paket Basic (Rp49.000/tahun) dan Premium (Rp149.000/tahun) opsional jika ingin fitur lebih lengkap.' },
  {
    q: 'Apa bedanya paket Gratis, Basic, dan Premium?',
    a: 'Gratis: 1 katalog, 5 produk, tema default. Basic: 50 produk, warna katalog custom, artikel usaha, badge UMKM Terverifikasi. Premium: katalog & produk tanpa batas, subdomain sendiri, banner promosi, statistik pengunjung, dan prioritas tampil di homepage.',
  },
]

type CatalogEntry = {
  id: number
  name: string
  slug: string
  logoUrl?: string | null
  description?: string | null
  viewCount?: number
  catalogUrl: string
  plan?: string
  badge?: string | null
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium text-gray-900">{question}</span>
        <ChevronDown
          className={`w-5 h-5 text-indigo-600 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-4 text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-3">
          {answer}
        </div>
      )}
    </div>
  )
}

export default function PromoPage() {
  useEffect(() => {
    document.title = 'Katalog Online UMKM Indonesia — Mulai Gratis'

    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'description'
      document.head.appendChild(meta)
    }
    meta.content =
      'Buat katalog online untuk usaha Anda — paket Gratis Rp0 selamanya. Tampilkan produk, profil usaha, dan WhatsApp dalam satu link. Upgrade ke Basic atau Premium kapan saja.'

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = 'https://umkmindonesia.my.id/promo'

    return () => {
      document.title = 'UMKM Indonesia - Katalog Pro'
    }
  }, [])

  const { data: catalogs = [], isLoading } = useQuery<CatalogEntry[]>({
    queryKey: ['brands', 'public', 'catalogs', 'promo'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/brands/public/catalogs`)
      if (!res.ok) throw new Error('Gagal memuat katalog')
      return res.json()
    },
  })

  const showcaseCatalogs = catalogs.slice(0, 6)

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />

        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative z-10"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeInUp}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-400/95 text-emerald-950 rounded-full mb-4 font-semibold text-sm shadow-lg">
                <Gift className="w-4 h-4" />
                PAKET GRATIS PERMANEN — Rp0
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-5 leading-tight">
                Buat Katalog Online Untuk Usaha Anda
              </h1>
              <p className="text-lg md:text-xl text-white/90 mb-6 leading-relaxed">
                Daftar gratis, langsung punya katalog online. Tampilkan produk, profil usaha, dan WhatsApp dalam satu link.
              </p>

              <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 mb-8 border border-white/20">
                <p className="text-sm md:text-base text-white/95 leading-relaxed">
                  <strong>Paket Gratis</strong> aktif otomatis setelah daftar — tanpa kartu kredit, tanpa kode referral.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={REGISTER_URL}>
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-white text-indigo-600 hover:bg-white/90 px-8 py-6 text-lg font-semibold shadow-xl"
                  >
                    Daftar Gratis Sekarang
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="#contoh-katalog">
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                  >
                    Lihat Contoh Katalog
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Hero Visual */}
            <motion.div variants={fadeInUp} className="hidden lg:block">
              <div className="relative">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-2xl">
                  <div className="bg-white rounded-xl overflow-hidden shadow-lg">
                    <div className="bg-indigo-600 px-4 py-3 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
                        U
                      </div>
                      <div>
                        <p className="text-white text-sm font-semibold">Usaha Anda</p>
                        <p className="text-white/70 text-xs">umkmindonesia.my.id/catalog/usaha-anda</p>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        {['Produk 1', 'Produk 2', 'Produk 3', 'Produk 4'].map((p) => (
                          <div key={p} className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                            <div className="aspect-square bg-gradient-to-br from-indigo-100 to-purple-100 rounded-md mb-1.5" />
                            <p className="text-xs font-medium text-gray-800 truncate">{p}</p>
                            <p className="text-xs text-indigo-600 font-semibold">Rp 99.000</p>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2 border border-green-100">
                        <MessageCircle className="w-4 h-4 text-green-600" />
                        <span className="text-xs text-green-800 font-medium">Hubungi via WhatsApp</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 bg-amber-400 text-amber-950 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                  Satu link, semua produk ✨
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Contoh Katalog — premium di atas */}
      <section id="contoh-katalog" className="py-16 md:py-20 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              Lihat Contoh Katalog yang Sudah Aktif
            </h2>
            <p className="text-gray-600">
              Katalog berbadge Premium ditampilkan terlebih dahulu — inspirasi dari usaha yang sudah go digital
            </p>
          </motion.div>

          <CatalogShowcaseGrid catalogs={showcaseCatalogs} isLoading={isLoading} />
        </div>
      </section>

      {/* Masalah */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              Masih Mengalami Masalah Ini?
            </h2>
          </motion.div>

          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {painPoints.map((point, idx) => (
              <motion.div key={idx} variants={fadeInUp}>
                <Card className="h-full border border-red-100 bg-white shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-5 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                      <point.icon className="w-5 h-5 text-red-500" />
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed pt-2">{point.text}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Solusi */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              Solusinya: Website Katalog Online
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              UMKM Indonesia membantu Anda punya halaman katalog profesional tanpa perlu bikin website sendiri.
            </p>
          </motion.div>

          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {benefits.map((benefit, idx) => (
              <motion.div key={idx} variants={fadeInUp}>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-indigo-50/60 border border-indigo-100 h-full">
                  <div className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">{benefit}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="text-center mt-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Link to={REGISTER_URL}>
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-white px-8">
                Mulai Buat Katalog Gratis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Cara Kerja */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">Cara Kerja</h2>
            <p className="text-gray-600">Hanya 5 langkah, katalog Anda siap dibagikan</p>
          </motion.div>

          <motion.div
            className="flex flex-col md:flex-row items-stretch md:items-start justify-center gap-4 md:gap-2"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {steps.map((step, idx) => (
              <motion.div key={idx} variants={fadeInUp} className="flex md:flex-col items-center gap-3 md:gap-4 flex-1 max-w-xs mx-auto md:max-w-none">
                <div className="relative flex md:flex-col items-center gap-3 md:gap-4 w-full">
                  <div className="w-14 h-14 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-lg shrink-0">
                    <step.icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 md:text-center">
                    <span className="text-xs font-bold text-indigo-600 uppercase tracking-wide">
                      Langkah {idx + 1}
                    </span>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">{step.label}</p>
                  </div>
                  {idx < steps.length - 1 && (
                    <ArrowRight className="hidden md:block absolute -right-3 top-5 w-5 h-5 text-indigo-300" />
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Paket Harga */}
      <section className="py-16 md:py-20 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">Pilih Paket Sesuai Kebutuhan</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Mulai dari <strong>Gratis Rp0</strong> — upgrade ke Basic atau Premium kapan saja jika usaha Anda berkembang.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PLANS.map((plan) => (
              <Card
                key={plan.id}
                className={`h-full ${plan.popular ? 'border-2 border-indigo-500 shadow-lg' : 'border shadow-sm'}`}
              >
                <CardContent className="p-6 flex flex-col h-full">
                  <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                  <p className="mt-4 text-3xl font-bold text-gray-900">
                    {plan.price === 0 ? 'Rp0' : `Rp${(plan.price / 1000).toFixed(0)}Rb`}
                    {plan.price > 0 && <span className="text-sm font-normal text-gray-500">/tahun</span>}
                  </p>
                  <ul className="mt-4 space-y-2 flex-1">
                    {plan.features.slice(0, 5).map((f) => (
                      <li key={f} className="text-sm text-gray-600 flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link to={plan.price === 0 ? REGISTER_URL : '/register'} className="block mt-6">
                    <Button
                      className={`w-full ${plan.popular ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                      variant={plan.popular ? 'default' : 'outline'}
                    >
                      {plan.price === 0 ? 'Daftar Gratis' : `Mulai ${plan.name}`}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              Pertanyaan yang Sering Diajukan
            </h2>
          </motion.div>

          <motion.div
            className="space-y-3"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {faqs.map((faq) => (
              <motion.div key={faq.q} variants={fadeInUp}>
                <FaqItem question={faq.q} answer={faq.a} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Penutup */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-indigo-600 to-purple-600">
        <motion.div
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <div className="flex justify-center mb-6">
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <Smartphone className="w-4 h-4" />
              <Link2 className="w-4 h-4" />
              <span>Bisa diakses dari HP & komputer</span>
            </div>
          </div>

          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Siap Membuat Katalog Online Untuk Usaha Anda?
          </h2>
          <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Mulai gratis sekarang — paket Gratis Rp0 aktif otomatis tanpa kode referral.
          </p>
          <Link to={REGISTER_URL}>
            <Button
              size="lg"
              className="bg-white text-indigo-600 hover:bg-white/90 px-10 py-6 text-lg font-semibold shadow-xl"
            >
              Daftar Gratis Sekarang
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </motion.div>
      </section>

      <SiteFooter />
    </div>
  )
}
